import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Grid, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import './refundTab.scss';
import SoftButton from 'components/SoftButton';
import HelpIcon from '@mui/icons-material/Help';
import useFetchRefund from './helpers/useFetchRefund';
import { policyCancellationReasons } from 'layouts/home/policyCancellation/const/policyCancellationReasons';
import { policyCancellationMethodReceived } from 'layouts/home/policyCancellation/const/policyCancellationMethodReceived';
import { policyCancellationDelayReasons } from 'layouts/home/policyCancellation/const/policyCancellationDelayReasons';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SoftSelect from 'components/SoftSelect';
import { policyRefundMethods } from './const/policyRefundMethods';
import useUpddateRefund from './helpers/useUpdateRefund';
import CurrencyComponent from 'shared/components/Currency/Currency';

const RefundTab = ({ policyDetails, setRefundStatus, refundStatus }) => {
  const { t } = useTranslation();
  const { refund, fetchRefund } = useFetchRefund();

  const { updatedRefund, updateRefund } = useUpddateRefund();

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      customerRefundDate: null,
      refundMethod: null,
    },
  });

  const customerRefundDate = watch('customerRefundDate');
  const refundMethod = watch('refundMethod');

  const minCustomerRefundDate = dayjs(policyDetails?.policy?.cancellationDate);
  const maxCustomerRefundDate = dayjs();

  const showEditFields = useMemo(() => {
    return (
      (!refund?.customerRefundDate || !refund?.refundMethod) &&
      (!updatedRefund?.customerRefundDate || !updatedRefund?.refundMethod)
    );
  }, [refund, updatedRefund]);

  const isPolicyCancelled = useMemo(() => {
    return policyDetails?.policy?.status?.label === 'cancelled';
  }, [policyDetails?.policy?.status?.label]);

  const showRefundDetails = useMemo(() => {
    return refund && refund?.status !== 'REISSUE';
  }, [refund]);

  useEffect(() => {
    const fetchRefundData = async () => {
      if (policyDetails?.policy?.policyNumber && isPolicyCancelled) {
        const refundData = await fetchRefund(
          policyDetails?.policy?.policyNumber,
        );
        if (refundData?.status) {
          setRefundStatus(refundData.status);
        }
      }
    };

    fetchRefundData();
  }, [
    policyDetails?.policy?.policyNumber,
    isPolicyCancelled,
    fetchRefund,
    setRefundStatus,
  ]);

  useEffect(() => {
    if (updatedRefund?.status && updatedRefund?.status !== refundStatus) {
      setRefundStatus(updatedRefund.status);
    }
  }, [updatedRefund?.status]);

  const reason = useMemo(
    () => policyCancellationReasons.find((r) => r.value === refund?.reason),
    [refund?.reason],
  );

  const requestMethod = useMemo(
    () =>
      policyCancellationMethodReceived.find(
        (r) => r.value === refund?.requestMethod,
      ),
    [refund?.requestMethod],
  );
  const delayReason = useMemo(
    () =>
      policyCancellationDelayReasons.find(
        (r) => r.value === refund?.delayReason,
      ),
    [refund?.delayReason],
  );

  const refundMethodSelected = useMemo(
    () =>
      policyRefundMethods.find((r) =>
        refund?.refundMethod
          ? r.value === refund?.refundMethod
          : r.value === updatedRefund?.refundMethod,
      ),
    [refund?.refundMethod, updatedRefund?.refundMethod],
  );

  const getCancellationValues = () => {
    return {
      requestDate: refund?.requestDate
        ? format(new Date(refund?.requestDate), 'dd/MM/yyyy')
        : undefined,
      reason: reason
        ? t(reason.label)
        : refund?.reason === 'not_eligible'
          ? t('POLICY_CANCELLATION.reasons.notEligible')
          : refund?.reason,
      cancellationDate: refund?.cancellationDate
        ? format(new Date(refund?.cancellationDate), 'dd/MM/yyyy')
        : undefined,
      delayReason: refund?.delayReasonOther
        ? `${t(delayReason?.label)} : ${refund?.delayReasonOther}`
        : t(delayReason?.label),
      refundDueDate: refund?.refundDueDate
        ? format(new Date(refund?.refundDueDate), 'dd/MM/yyyy')
        : undefined,
      amount: refund?.amount ? (
        <CurrencyComponent
          value={refund.amount.amount}
          countryCode={refund.amount.currency}
        />
      ) : undefined,
      blank: '',
      requestMethod: t(requestMethod?.label),
    };
  };

  const cancellationValues = getCancellationValues();

  const handleDateChange = (date) => {
    if (!date) {
      return;
    }

    if (
      date.isBefore(minCustomerRefundDate) ||
      date.isAfter(maxCustomerRefundDate) ||
      !dayjs(date).isValid()
    ) {
      setError('customerRefundDate', {
        message: t('POLICY_DETAILS.REFUND.validation.dateNotInRange'),
      });
      return;
    }
    clearErrors('customerRefundDate');
    setValue('customerRefundDate', date);
  };

  const handleRefundMethodChange = (method) => {
    setValue('refundMethod', method);
  };

  const handleModifyPolicy = async (formData) => {
    const { customerRefundDate, refundMethod } = formData;

    await updateRefund(policyDetails?.policy?.policyNumber, {
      customerRefundDate: dayjs(customerRefundDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      refundMethod,
    });
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isSaveButtonDisabled =
    !customerRefundDate || !refundMethod || hasErrors;

  if (!isPolicyCancelled) {
    return (
      <div className="refundTab">
        <div>
          <div className="header">
            <h3>{t('POLICY_DETAILS.REFUND.noRefund.title')}</h3>
          </div>
          <div className="text-value">
            {t('POLICY_DETAILS.REFUND.noRefund.policyNotCancelled')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="refundTab">
      <div>
        <div className="header">
          <h3>{t('POLICY_DETAILS.REFUND.titleCancellation')}</h3>
        </div>
        <Grid container spacing={2}>
          {Object.entries(cancellationValues).map(([name, value], index) => (
            <Grid item xs={12} md={6} key={index} className="field-container">
              {name !== 'blank' ? (
                <>
                  <div className="label-text">
                    {t(`POLICY_DETAILS.REFUND.${name}`)}
                  </div>
                  <div
                    className={`text-value ${
                      name === 'refundDueDate' ? 'danger' : ''
                    }`}
                  >
                    {value}
                  </div>
                </>
              ) : null}
            </Grid>
          ))}
        </Grid>
      </div>
      {showRefundDetails && (
        <div className="refund-details">
          <div className="header">
            <h3>{t('POLICY_DETAILS.REFUND.titleRefund')}</h3>
          </div>
          <form onSubmit={handleSubmit(handleModifyPolicy)}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} className="field-container">
                <div className="label-text">
                  {t(`POLICY_DETAILS.REFUND.customerRefundDate`)}
                  <Tooltip
                    title={t(`POLICY_DETAILS.REFUND.customerRefundDateTooltip`)}
                    placement="top"
                    style={{ marginLeft: '8px' }}
                  >
                    <HelpIcon color="black" />
                  </Tooltip>
                </div>
                <div className="text-value">
                  {showEditFields && (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('POLICY_DETAILS.REFUND.customerRefundDateLabel')}
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          required
                          minDate={minCustomerRefundDate}
                          maxDate={maxCustomerRefundDate}
                          format="DD-MM-YYYY"
                          sx={{ display: 'flex', maxWidth: '15rem' }}
                          onChange={handleDateChange}
                          slotProps={{
                            textField: {
                              error: !!errors.customerRefundDate,
                              helperText:
                                errors.customerRefundDate?.message || '',
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </>
                  )}
                  {!showEditFields &&
                    format(
                      new Date(
                        refund?.customerRefundDate
                          ? refund?.customerRefundDate
                          : updatedRefund?.customerRefundDate,
                      ),
                      'dd/MM/yyyy',
                    )}
                </div>
              </Grid>
              <Grid item xs={12} className="field-container">
                <div className="label-text">
                  {t(`POLICY_DETAILS.REFUND.refundMethodLabel`)}
                </div>
                <div className="text-value">
                  {showEditFields && (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('POLICY_DETAILS.REFUND.refundMethodLabel')}
                      </Typography>
                      <SoftSelect
                        required
                        placeholder={t(
                          'POLICY_DETAILS.REFUND.refundMethodPlaceholder',
                        )}
                        options={policyRefundMethods}
                        onChange={handleRefundMethodChange}
                        className="reason-dropdown"
                      />
                    </>
                  )}
                  {!showEditFields && t(refundMethodSelected?.label)}
                </div>
              </Grid>
              {showEditFields && (
                <Grid item xs={12} className="field-container">
                  <SoftButton
                    variant="contained"
                    style={{ backgroundColor: '#0a76ec', color: 'white' }}
                    type="submit"
                    disabled={isSaveButtonDisabled}
                  >
                    {t('POLICY_DETAILS.REFUND.saveButton')}
                  </SoftButton>
                </Grid>
              )}
            </Grid>
          </form>
        </div>
      )}
    </div>
  );
};

RefundTab.propTypes = {
  policyDetails: PropTypes.object,
  setRefundStatus: PropTypes.func,
  refundStatus: PropTypes.string,
};

export default RefundTab;
